import React, { useState, useEffect } from "react";
import PrintIcon from "../../../../../assets/images/Print-Icon.svg";
import DownloadIcon from "../../../../../assets/images/Download-Icon.svg";
import { useTranslation } from "react-i18next";
import { heicTo } from "heic-to";
import { GeneralButton } from "../../../../../components";
import { handlePrint, handleDownloadPdf } from "../../../../../utils/fileUtils";

interface IMedicalFilePreview {
  file?: string | null;
  type?: "IMAGE" | "PDF";
  contentType?: string | null;
  isLoading?: boolean;
  Name: string;
}

const stripDataUriPrefix = (value?: string | null): string => {
  if (!value) return "";
  const commaIdx = value.indexOf(",");
  return value.startsWith("data:") && commaIdx !== -1
    ? value.substring(commaIdx + 1)
    : value;
};

const base64ToBytes = (base64: any) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const looksLikeHeif = (bytes: Uint8Array): boolean => {
  if (bytes.length < 12) return false;
  const brand = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
  const subBrand = String.fromCharCode(
    bytes[8],
    bytes[9],
    bytes[10],
    bytes[11],
  );
  if (brand !== "ftyp") return false;
  return /^(heic|heix|hevc|hevx|mif1|msf1|heif|heis|heim|hevm|hevs)$/.test(
    subBrand,
  );
};

const detectRealImageType = (
  bytes: Uint8Array,
): "jpeg" | "png" | "gif" | "webp" | null => {
  if (bytes.length < 12) return null;
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return "jpeg";
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e) return "png";
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return "gif";
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  )
    return "webp";
  return null;
};

const MedicalFilePreview: React.FC<IMedicalFilePreview> = ({
  Name,
  file,
  type,
  contentType,
  isLoading,
}) => {
  const [zoom, setZoom] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [convertedImage, setConvertedImage] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionFailed, setConversionFailed] = useState(false);
  const [fallbackMime, setFallbackMime] = useState<string | null>(null);

  const { t } = useTranslation();

  const rawBase64 = stripDataUriPrefix(file);
  const normalizedCt = (contentType ?? "").toLowerCase();
  const lowerName = Name?.toLowerCase() ?? "";

  const isHeicByMeta =
    normalizedCt.includes("heic") ||
    normalizedCt.includes("heif") ||
    lowerName.endsWith(".heic") ||
    lowerName.endsWith(".heif");

  useEffect(() => {
    let isCancelled = false;
    let objectUrl: string | null = null;

    const convertHeic = async () => {
      if (!rawBase64) return;

      setIsConverting(true);
      setConversionFailed(false);
      setConvertedImage(null);
      setFallbackMime(null);

      try {
        const bytes = base64ToBytes(rawBase64);

        const realType = detectRealImageType(bytes);
        if (realType) {
  
          setFallbackMime(`image/${realType}`);
          setIsConverting(false);
          return;
        }

        const isHeifByBytes = looksLikeHeif(bytes);
        const shouldConvert = isHeifByBytes || isHeicByMeta;

        
        if (!shouldConvert) {
          setIsConverting(false);
          return;
        }

        const heicBlob = new Blob([bytes], { type: "image/heic" });
        const pngBlob = await heicTo({
          blob: heicBlob,
          type: "image/png",
          quality: 1,
        });

        if (isCancelled) return;
        if (!pngBlob || pngBlob.size === 0) throw new Error("empty blob");

        objectUrl = URL.createObjectURL(pngBlob);
        setConvertedImage(objectUrl);
      } catch (error) {
        console.error("[MedicalFilePreview] HEIC conversion failed:", error);
        if (!isCancelled) setConversionFailed(true);
      } finally {
        if (!isCancelled) setIsConverting(false);
      }
    };

    convertHeic();

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rawBase64, isHeicByMeta, Name, contentType]);

  const handleWheelZoom = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + zoomDelta, 0.5), 3));
  };

  const headerButtonStyle =
    "!bg-white border border-grayishBlue !text-black !font-normal";

  if (isLoading) {
    return (
      <div className="mt-5 flex min-h-[40vh] w-full flex-col items-center justify-center gap-4">
        {/* <Loader /> */}
      </div>
    );
  }

  const renderPreview = () => {
    const mimeType = type === "PDF" ? "application/pdf" : "image/png";

    if (type === "IMAGE") {
      let imageSrc: string | null = null;
      if (convertedImage) imageSrc = convertedImage;
      else if (fallbackMime)
        imageSrc = `data:${fallbackMime};base64,${rawBase64}`;
      else if (!isHeicByMeta) imageSrc = `data:${mimeType};base64,${rawBase64}`;

      return (
        <div className="relative flex flex-col gap-4" onWheel={handleWheelZoom}>
          <div
            className="flex items-center justify-center overflow-hidden"
            style={{ width: "100%", height: "300px", background: "#f0f0f0" }}
          >
            {isConverting ? (
              <p>{t("loading")}...</p>
            ) : conversionFailed ? (
              <p className="px-4 text-center text-sm text-red-500">
                {t("preview_not_available")}
              </p>
            ) : imageSrc ? (
              <img
                src={imageSrc}
                alt="Preview"
                style={{
                  transform: `scale(${zoom})`,
                  transition: "transform 0.3s ease",
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              />
            ) : null}
          </div>
          <p className="text-center">{Name}</p>
        </div>
      );
    }

    if (type === "PDF") {
      const blob = new Blob([base64ToBytes(rawBase64)], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      return (
        <div className="flex flex-col gap-4">
          <iframe
            src={blobUrl}
            style={{ width: "100%", height: "300px" }}
            title="PDF Preview"
          />
          <p>{Name}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex w-full flex-col">
      {renderPreview()}
      <div className="mt-4 flex justify-end gap-2">
        <GeneralButton
          color="lite"
          icon={PrintIcon}
          className={headerButtonStyle}
          onClick={() => handlePrint(rawBase64, type, setIsPrinting)}
          title={t("print")}
          isLoading={isPrinting}
        />
        <GeneralButton
          color="lite"
          icon={DownloadIcon}
          className={headerButtonStyle}
          onClick={() =>
            handleDownloadPdf(rawBase64, Name, type, setIsDownloading)
          }
          title={t("download_as_pdf")}
          isLoading={isDownloading}
        />
      </div>
    </div>
  );
};

export default MedicalFilePreview;
